import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { ShieldAlert, ChevronRight, Loader2 } from "lucide-react";

/**
 * Sprint 77 · operational alert banner
 * Shows on Admin/Founder/CEO dashboard home when open anomalies exist.
 * Auto-refreshes every 60s.
 */
export default function AnomalyAlertBanner({ role }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!["admin", "founder", "ceo"].includes(role)) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/anomalies/summary");
        if (alive) setData(data);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [role]);

  if (!["admin", "founder", "ceo"].includes(role)) return null;
  if (loading) return null;
  const open = data?.by_status?.open || 0;
  const high = data?.by_severity?.high || 0;
  if (open === 0) return null;

  const isHigh = high > 0;
  const containerCls = isHigh
    ? "bg-rose-50 border-rose-200 text-rose-900"
    : "bg-amber-50 border-amber-200 text-amber-900";

  return (
    <button
      type="button"
      onClick={() => navigate("/dashboard/anomalies")}
      className={`w-full text-left rounded-2xl border ${containerCls} px-4 py-3 flex items-center gap-3 hover:shadow-md transition-shadow animate-fadeInUp`}
      data-testid="anomaly-alert-banner"
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isHigh ? "bg-rose-500" : "bg-amber-500"}`}>
        <ShieldAlert className="h-5 w-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold">
          {open} open anomal{open === 1 ? "y" : "ies"} need review
          {high > 0 && <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 bg-rose-600 text-white text-[10px] font-bold rounded-md uppercase">{high} High</span>}
        </p>
        <p className="text-xs opacity-80 mt-0.5">
          Last scan: {data?.last_run?.ts ? new Date(data.last_run.ts).toLocaleString() : "—"} · click to review
        </p>
      </div>
      <ChevronRight className="h-5 w-5 opacity-70 shrink-0" />
    </button>
  );
}
